import {
  useOnboardingAnalytics,
  useOnboardingStepView,
} from "analytics/onboarding";
import usePracticeExchange from "features/onboarding/guided/usePracticeExchange";
import { useSymbolsMetadata } from "@shared/utils/hooks/misc";
import useOnboardingPracticeAccount from "features/onboarding/guided/useOnboardingPracticeAccount";
import { PRACTICE_SYMBOLS } from "features/onboarding/guided/intent";
import { useContext } from "react";
import styled from "styled-components";
import { NextArrow } from "features/onboarding/common/OnboardingIcons";
import Button from "common/Buttons/Button";
import Text from "common/Typography/Text";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { OnboardingContext } from "@shared/features/onboarding/Onboarding.context";
import { OnboardingDetailedStep } from "@shared/features/onboarding/types";
import { LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  ONBOARDING_BROWSE_BOTS_ID,
  ONBOARDING_INTENT_CONTINUE_ID,
  ONBOARDING_PREVIEW_BOT_ID,
} from "testIds";
import useSceneWalkthrough from "features/onboarding/guided/walkthrough/useSceneWalkthrough";
import useStartLive from "features/onboarding/guided/useStartLive";
import WalkthroughLayout from "features/onboarding/guided/walkthrough/WalkthroughLayout";
import {
  WalkthroughAlternatives,
  WalkthroughLiveRow,
} from "features/onboarding/guided/walkthrough/WalkthroughActions";
import WalkthroughMeta from "features/onboarding/guided/walkthrough/WalkthroughMeta";
import WalkthroughPanel from "features/onboarding/guided/walkthrough/WalkthroughPanel";
import WalkthroughSteps from "features/onboarding/guided/walkthrough/WalkthroughSteps";
import { WALKTHROUGH_STEPS } from "features/onboarding/guided/walkthrough/steps";

const GuidedBotChoice = ({ onBrowse: browse }: { onBrowse: () => void }) => {
  useOnboardingStepView("method_walkthrough");
  const track = useOnboardingAnalytics();
  const onBrowse = (): void => {
    track("Walkthrough Choice", {
      choice: "browse",
      onboarding_path: "demo_browse",
    });
    browse();
  };
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const metadata = useSymbolsMetadata();
  const isLoading = !metadata.isSuccess;
  const { changeStep, finishOnboardingLoading } =
    useContext(OnboardingContext)!;
  const { exchange } = usePracticeExchange();
  const { account } = useOnboardingPracticeAccount(exchange);
  const configureOwnBot = (prepared = false): void => {
    track("Walkthrough Choice", {
      choice: prepared ? "preview_bot" : "build_own",
      onboarding_path: prepared ? "demo_prepared" : "demo_build_own",
    });
    changeStep(
      account
        ? OnboardingDetailedStep.TRADING_METHOD
        : OnboardingDetailedStep.CONNECT_EXCHANGE,
      {
        guided: "1",
        prepareDemo: account ? "" : "1",
        exchange,
        "account-id": account ? String(account.client_id) : "",
        method: "dca",
        setup: "1",
        preset: prepared ? "dca" : "",
        botUUID: "",
      }
    );
  };
  const symbols = account
    ? metadata.getAccountActiveSymbols(account.client_id)
    : PRACTICE_SYMBOLS;
  const example = PRACTICE_SYMBOLS.find((symbol) => symbols?.includes(symbol));
  const onPreview = (): void => configureOwnBot(true);
  const playback = useSceneWalkthrough("dca", 4);
  const startLive = useStartLive();
  const { stage } = playback;
  return (
    <LoadingSpinnerOverlay
      isLoading={isLoading}
      wrap={true}
      wrapStyle={{ height: "auto" }}
    >
      <WalkthroughLayout
        title={t("guided.bot_title")}
        subtitle={t("intent.walkthrough_description")}
        meta={<WalkthroughMeta demo pair={example} />}
        story={
          <>
            <WalkthroughSteps
              steps={
                playback.steps ??
                WALKTHROUGH_STEPS.dca.map((key) => ({ title: t(key) }))
              }
              progressRef={playback.barRef}
              stage={stage}
              onSelect={playback.goTo}
            />
            {!example && (
              <NoExample type="medium">
                {t("guided.no_example_description")}
              </NoExample>
            )}
          </>
        }
        panel={
          <WalkthroughPanel
            intent="dca"
            stage={stage}
            total={4}
            label={t(`revision.dca_event${stage as 0 | 1 | 2 | 3 | 4}`)}
            caption={
              stage === 0
                ? t("intent.waiting")
                : playback.steps
                ? playback.steps[stage - 1].description!
                : t(`revision.dca_event${stage as 1 | 2 | 3 | 4}`)
            }
            playing={playback.playing}
            {...playback.panel}
            togglePlayback={playback.togglePlayback}
            advance={playback.advance}
            actionLabel={
              stage === 4
                ? t("intent.replay")
                : stage === 3
                ? t("revision.dca_rebound")
                : t("revision.dca_fill", { entry: stage + 1 })
            }
            chartRef={playback.chartRef}
          />
        }
        actions={
          <>
            <Button
              type="primary"
              size="large"
              onClick={example ? onPreview : onBrowse}
              data-testid={
                example ? ONBOARDING_PREVIEW_BOT_ID : ONBOARDING_BROWSE_BOTS_ID
              }
            >
              {t(example ? "guided.preview_bot" : "guided.browse_bots")}{" "}
              <NextArrow />
            </Button>
            <WalkthroughAlternatives
              routes={[
                ...(example
                  ? [
                      {
                        label: t("walkthrough.browse_presets"),
                        onClick: onBrowse,
                        testId: ONBOARDING_BROWSE_BOTS_ID,
                      },
                    ]
                  : []),
                {
                  label: t(
                    example
                      ? "walkthrough.build_own"
                      : "walkthrough.build_own_demo"
                  ),
                  onClick: () => configureOwnBot(),
                  testId: ONBOARDING_INTENT_CONTINUE_ID,
                  loading: finishOnboardingLoading,
                },
              ]}
            />
          </>
        }
        footer={<WalkthroughLiveRow onClick={startLive} />}
      />
    </LoadingSpinnerOverlay>
  );
};

export default GuidedBotChoice;

const NoExample = styled(Text)`
  && {
    margin: 0;
    color: ${(p) => p.theme.colors.secondaryText};
    font-size: 14px;
    line-height: 1.55;
  }
`;
